import React, { useState, useEffect } from 'react';
import DashboardHeader from './maindashboard/DashboardHeader';
import KPIStats from './maindashboard/KPIStats';
import DashboardCharts from './maindashboard/DashboardCharts';
import ActiveShipsModal from './maindashboard/ActiveShipsModal';
import ProjectDashboardModal from './ProjectDashboardModal';
import ChartDetailsModal from './maindashboard/ChartDetailsModal';
import FullScreenChart from './maindashboard/FullScreenChart';
import ReusableProjectTable from './ProjectsTable';
import { getRequest } from '../services/apiService';

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [showDashboardModal, setShowDashboardModal] = useState(false);
  const [isFullView, setIsFullView] = useState(false);
  const [selectedProject, setSelectedProject] = useState('1');
  const [selectedShip, setSelectedShip] = useState('12');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [projects, setProjects] = useState([]);
  const [ships, setShips] = useState([]);
  const [sCurveData, setSCurveData] = useState([]);
  const [sCurveLoading, setSCurveLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [visibleCharts, setVisibleCharts] = useState([
    'sCurve',
    'projectStatus',
    'criticalPath',
    'financialOverview',
    'projectTable',
    'physicalProgress'
  ]);
  const [fullScreenChart, setFullScreenChart] = useState(null);
  const [showChartDetails, setShowChartDetails] = useState(false);
  const [selectedChartType, setSelectedChartType] = useState(null);
  const [selectedChartData, setSelectedChartData] = useState(null);
  const [isFinancialTable, setIsFinancialTable] = useState(false);
  const [projectStatusApiData, setProjectStatusApiData] = useState(null);
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [showActiveShipsModal, setShowActiveShipsModal] = useState(false);

  // Fetch projects list
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await getRequest('/master/projects/');
        if (response?.data) {
          setProjects(response.data);
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setLoadingProjects(false);
      }
    };
    fetchProjects();
  }, []);

  // Fetch ships when project changes
  useEffect(() => {
    if (!selectedProject) {
      setShips([]);
      setSelectedShip('');
      return;
    }
    const fetchShips = async () => {
      try {
        const response = await getRequest(`/master/projects/?parent_id=${selectedProject}`);
        if (response?.data) {
          setShips(
            response.data.map(ship => ({
              id: ship.id,
              name: ship.name
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching ships:", error);
        setShips([]);
      }
    };
    fetchShips();
  }, [selectedProject]);
  
  useEffect(() => {
    const fetchDashboardSummary = async () => {
      if (!selectedProject) {
        setDashboardSummary(null);
        return;
      }

      try {
        const response = await getRequest(
          `/transaction/dashboard-summary/?project_id=${selectedProject}`
        );
        setDashboardSummary(response);
      } catch (error) {
        console.error("Error fetching dashboard summary:", error);
        setDashboardSummary(null);
      }
    };

    fetchDashboardSummary();
  }, [selectedProject]);

  useEffect(() => {
    const fetchProjectStatus = async () => {
      try {
        const response = await getRequest(`/transaction/project-status/`);
        if (response && typeof response === 'object') {
          setProjectStatusApiData(response);
        } else {
          setProjectStatusApiData({});
        }
      } catch (error) {
        setProjectStatusApiData({});
      }
    };
    fetchProjectStatus();
  }, []);

  // Fetch S-Curve data
  useEffect(() => {
    const fetchSCurveData = async () => {
      if (!selectedProject) {
        setSCurveData([]);
        return;
      }

      setSCurveLoading(true);
      try {
        let url = `/transaction/api/project/${selectedProject}/s-curve/`;
        if (selectedShip) {
          url += `?ship_id=${selectedShip}`;
        }
        const response = await getRequest(url);

        if (response) {
          const transformed = transformSCurveData(response, selectedYear || undefined);
          setSCurveData(transformed);
        }
      } catch (error) {
        console.error('Error fetching S-Curve data:', error);
        setSCurveData([]);
      } finally {
        setSCurveLoading(false);
      }
    };

    fetchSCurveData();
  }, [selectedProject, selectedShip, selectedYear]);

  // Transform S-Curve API response to chart format
  const transformSCurveData = (apiData, year) => {
    const contractual = apiData['Contractual S-Curve']?.Summary || {};
    const revised = apiData['Revised S-Curve']?.Summary || {};
    const achieved = apiData['Achieved Progress %']?.Summary || {};

    const allMonths = [
      ...Object.keys(contractual),
      ...Object.keys(revised),
      ...Object.keys(achieved)
    ];
    const uniqueMonths = Array.from(new Set(allMonths)).sort();

    const filteredMonths = year
      ? uniqueMonths.filter(m => m.startsWith(year))
      : uniqueMonths;

    const allMonthsOfYear = year
      ? Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`)
      : uniqueMonths;

    const data = allMonthsOfYear.map(month => ({
      date: `${month}-01`,
      contractual: contractual[month] ?? null,
      revised: revised[month] ?? null,
      achieved: achieved[month] ?? null,
    }));

    return data;
  };

  const getChartData = (chartType) => {
    switch (chartType) {
      case 'sCurve':
        return sCurveData;
      case 'projectStatus':
        if (!projectStatusApiData) return [];
        const statuses = Object.keys(projectStatusApiData);
    
        const totalCount = statuses.reduce((sum, statusKey) => {
          const count = projectStatusApiData[statusKey]?.count;
          return sum + count;
        }, 0);

        return statuses.map(statusKey => {
          const statusInfo = projectStatusApiData[statusKey];
          let statusColor = 'bg-gray-500';
          if (statusKey === 'On Track') statusColor = 'bg-green-500';
          else if (statusKey === 'Delayed') statusColor = 'bg-yellow-500';
          else if (statusKey === 'At Risk') statusColor = 'bg-red-500';
          else if (statusKey === 'Completed') statusColor = 'bg-blue-500';

          return {
            status: statusKey,
            count: statusInfo?.count,
            percentage: totalCount > 0 ? parseFloat((((statusInfo?.count || 0) / totalCount) * 100).toFixed(1)) : 0,
            trend: 0,
            statusColor: statusColor,
            tasks: Array.isArray(statusInfo?.tasks) ? statusInfo.tasks.filter(task => typeof task === 'object' && task !== null) : []
          };
        });
      case 'criticalPath':
        return [
          {
            task: 'Production Planning',
            taskId: 'A2KA1',
            type: 'planning',
            startDate: '01/09/2022',
            endDate: '30/11/2022',
            duration: '90 days',
            status: 'completed',
            dependencies: []
          },
          {
            task: 'Assembly Line Setup',
            taskId: 'E1KXC',
            type: 'setup',
            startDate: '01/12/2022',
            endDate: '15/01/2023',
            duration: '45 days',
            status: 'completed',
            dependencies: ['A2KA1']
          },
          {
            task: 'Erection',
            taskId: 'A2KKD',
            type: 'block',
            startDate: '23/10/2022',
            endDate: '20/02/2023',
            duration: '120 days',
            status: 'in-progress',
            dependencies: ['A2KA1']
          },
          {
            task: 'Quality Testing',
            taskId: 'Q1TXB',
            type: 'testing',
            startDate: '21/02/2023',
            endDate: '22/03/2023',
            duration: '30 days',
            status: 'pending',
            dependencies: ['A2KKD', 'E1KXC']
          },
          {
            task: 'Final Integration',
            taskId: 'F1INT',
            type: 'integration',
            startDate: '23/03/2023',
            endDate: '22/04/2023',
            duration: '30 days',
            status: 'pending',
            dependencies: ['Q1TXB']
          }
        ];
      case 'financialOverview':
        return [
          {
            category: 'Equipment Cost',
            amount: 580,
            percentage: 29,
            status: 'On Track',
            trend: 2.5,
            breakdown: {
              allocated: 580,
              spent: 435,
              remaining: 145
            }
          },
          {
            category: 'Labor Cost',
            amount: 460,
            percentage: 23,
            status: 'At Risk',
            trend: -4.2,
            breakdown: {
              allocated: 460,
              spent: 380,
              remaining: 80
            }
          },
          {
            category: 'Material Cost',
            amount: 520,
            percentage: 26,
            status: 'On Track',
            trend: 1.8,
            breakdown: {
              allocated: 520,
              spent: 390,
              remaining: 130
            }
          },
          {
            category: 'Overhead Cost',
            amount: 180,
            percentage: 9,
            status: 'Moderate',
            trend: -1.5,
            breakdown: {
              allocated: 180,
              spent: 120,
              remaining: 60
            }
          },
          {
            category: 'Contingency',
            amount: 260,
            percentage: 13,
            status: 'On Track',
            trend: 0,
            breakdown: {
              allocated: 260,
              spent: 65,
              remaining: 195
            }
          }
        ];
      default:
        return [];
    }
  };

  return (
    <div className="h-screen flex flex-col">
      <DashboardHeader 
        projects={projects}
        ships={ships}
        loadingProjects={loadingProjects}
        selectedProject={selectedProject}
        setSelectedProject={setSelectedProject}
        selectedShip={selectedShip}
        setSelectedShip={setSelectedShip}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        visibleCharts={visibleCharts}
        setVisibleCharts={setVisibleCharts}
      />
      
      <div className="flex-1">
        <div className="p-4 h-full">
          <KPIStats 
            projects={projects}
            ships={ships}
            dashboardSummary={dashboardSummary}
            setShowActiveShipsModal={setShowActiveShipsModal}
          />
          
          <ReusableProjectTable
            onDashboardOpen={(data) => {
              setDashboardData(data);
              setShowDashboardModal(true);
            }}
            selectedProject={selectedProject}
            selectedYear={selectedYear}
          />
          
          <DashboardCharts
            visibleCharts={visibleCharts}
            sCurveData={sCurveData}
            sCurveLoading={sCurveLoading}
            selectedProject={selectedProject}
            selectedShip={selectedShip}
            selectedYear={selectedYear}
            projectStatusApiData={projectStatusApiData}
            setFullScreenChart={setFullScreenChart}
            setSelectedChartType={setSelectedChartType}
            setSelectedChartData={setSelectedChartData}
            setShowChartDetails={setShowChartDetails}
            getChartData={getChartData}
          />
        </div>
      </div>

      {/* Modals */}
      {showDashboardModal && (
        <ProjectDashboardModal
          project={dashboardData?.project}
          ship={dashboardData?.ship}
          yard={dashboardData?.yard}
          onClose={() => setShowDashboardModal(false)}
          isFullView={isFullView}
          toggleFullView={() => setIsFullView(!isFullView)}
        />
      )}
      
      {showActiveShipsModal && (
        <ActiveShipsModal 
          onClose={() => setShowActiveShipsModal(false)}
          ships={ships}
        />
      )}
      
      {fullScreenChart && (
        <FullScreenChart
          chartId={fullScreenChart}
          onClose={() => setFullScreenChart(null)}
          sCurveData={sCurveData}
          selectedYear={selectedYear}
          selectedProject={selectedProject}
          selectedShip={selectedShip}
        />
      )}
      
      <ChartDetailsModal
        isOpen={showChartDetails}
        onClose={() => setShowChartDetails(false)}
        chartType={selectedChartType}
        data={selectedChartData}
        isFinancialTable={isFinancialTable}
        setIsFinancialTable={setIsFinancialTable}
        selectedShip={selectedShip}
        setSelectedShip={setSelectedShip}
        projectStatusApiData={projectStatusApiData}
      />
    </div>
  );
};

export default Dashboard;